/**
 * useCompanyInfo - Hook for fetching company info from API
 *
 * Returns company information for use across public pages (terms, privacy, contact).
 * Provides sensible fallback values if API is unavailable.
 *
 * Caching strategy:
 * 1. Try to fetch fresh data from API
 * 2. On success, cache to localStorage (valid for 24 hours)
 * 3. On failure, try localStorage cache
 * 4. If no cache, use hardcoded fallback values
 */

import { useState, useEffect } from "react";
import { api, type PublicConfig } from "@/lib/api";

interface CompanyInfo {
  name: string;
  orgNumber: string;
  address: string;
  postalCode: string;
  city: string;
  email: string;
}

interface CompanyInfoData {
  company: CompanyInfo;
  isLoading: boolean;
  error: string | null;

  // Formatted strings for common use cases
  formattedOrgNumber: string; // "559XXX-XXXX" or "Org.nr: 559XXX-XXXX"
  formattedAddress: string; // "Address, PostalCode City"
  formattedCityOnly: string; // "Stockholm, Sverige"
}

interface CachedCompanyInfo {
  company: CompanyInfo;
  timestamp: number;
}

// Default fallback values
const DEFAULT_COMPANY: CompanyInfo = {
  name: "Kvanten AB",
  orgNumber: "",
  address: "",
  postalCode: "",
  city: "Stockholm",
  email: "info@kvanten.se",
};

// Cache settings
const CACHE_KEY = "kvanten_company_info_cache";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getCachedCompanyInfo(): CachedCompanyInfo | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: CachedCompanyInfo = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - data.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function setCachedCompanyInfo(company: CompanyInfo): void {
  try {
    const data: CachedCompanyInfo = {
      company,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors (quota exceeded, private browsing, etc.)
  }
}

export function useCompanyInfo(): CompanyInfoData {
  const [company, setCompany] = useState<CompanyInfo>(DEFAULT_COMPANY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchCompanyInfo = async () => {
      try {
        const res = await api.get<PublicConfig>("/config");
        if (mounted && res.success && res.data?.company) {
          const fetchedCompany: CompanyInfo = {
            name: res.data.company.name || DEFAULT_COMPANY.name,
            orgNumber: res.data.company.orgNumber || DEFAULT_COMPANY.orgNumber,
            address: res.data.company.address || DEFAULT_COMPANY.address,
            postalCode: res.data.company.postalCode || DEFAULT_COMPANY.postalCode,
            city: res.data.company.city || DEFAULT_COMPANY.city,
            email: res.data.company.email || DEFAULT_COMPANY.email,
          };
          setCompany(fetchedCompany);
          // Cache successful response
          setCachedCompanyInfo(fetchedCompany);
        }
      } catch {
        if (mounted) {
          setError("Kunde inte hämta företagsinfo");

          // Try to use cached values first
          const cached = getCachedCompanyInfo();
          if (cached) {
            setCompany(cached.company);
          }
          // If no cache, DEFAULT_COMPANY is already set
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCompanyInfo();

    return () => {
      mounted = false;
    };
  }, []);

  // Build formatted strings
  const formattedOrgNumber = company.orgNumber
    ? company.orgNumber
    : "";

  const addressParts = [company.address, company.postalCode, company.city].filter(Boolean);
  const formattedAddress = addressParts.join(", ");

  const formattedCityOnly = company.city ? `${company.city}, Sverige` : "Sverige";

  return {
    company,
    isLoading,
    error,
    formattedOrgNumber,
    formattedAddress,
    formattedCityOnly,
  };
}
